import React, { useState, useContext, useCallback, useEffect, useMemo } from 'react';
import { modeUIContext, showSuccessNotification, setIsEditingForm, showErrorNotification } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import produce from 'immer';
import { CircularProgress } from '@material-ui/core';
import { AppAPI, GatewayDefinition, GatewayDriver, RobotCommunicationType, RobotDefinition, RobotInfo } from '@moderepo/mode-apis';
import { BaseInfoCompProps, capitalizeAll, getErrorMessage, getRobotDefinitionByRobotClass, RCRobotInfo, RCRobotInfoProps, RobotInfoUpdatableInputs,
    ROBOT_ID_REGEX, SelectInputOption } from '@moderepo/mode-app-components';
import { ApiError, DeviceClass } from '@moderepo/mode-internal-apis';
import { fetchProjectDeviceById, ritzDataContext, selectProjectById, selectProjectDeviceById } from '@moderepo/mode-data-state-ritz';
import { createRobot, fetchRCProjectGatewayCatalog, fetchRobotById, fetchRobotCatalog, rcDataContext, selectProjectGatewayCatalog, selectRobotById,
    selectRobotCatalog, updateRobot } from '@moderepo/mode-data-state-robot-cloud';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectProjectDeviceClasses } from '../../utils/dataStateHooks';



interface InputErrors {
    id?: string | null;
    robotClass?: string | null;
    driverName?: string | null;
    overrideRobotDefinition?: string | null
}



export interface RCRobotInfoContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly robotId?: string;                                  // will be undefined if this container is used for creating new robot
    readonly homeId: number;
    readonly targetGatewayId?: number | undefined;              // This is deviceId/gatewayId of the gateway to link the NEW Robot to
    readonly newRobotSettings?: {                               // Only available if this container is used for creating new robot
        readonly onNewRobotCreated: ()=> void;
        readonly onNewRobotCanceled: ()=> void;
    }
    readonly minimalUI?: boolean;                               // Minimize the UI on Edit mode by hiding the not editable fields


    // This container used RCRobotInfo component to display user info and UserInfo component has its own customizable props which can be
    // set through this props.
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
        readonly formProps?: BaseInfoCompProps['formProps'] | undefined;
    } | undefined;
}


/**
 * This is the container for the RobotInfoComp. It provides the logic for the RobotInfoComp which is a dumb component that does not
 * do anything other than displaying data. This container will take care of all the logic e.g. edit Robot, translate, etc...
 * @param props
 */
export const RCRobotInfoContainer = (props: RCRobotInfoContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const gatewayCatalog = selectProjectGatewayCatalog(rcDataState, props.projectId);
    const robotCatalog = selectRobotCatalog(rcDataState, props.projectId);
    const deviceClasses = useSelectProjectDeviceClasses(props.projectId);


    // Get the RobotInfo for the provided props.robotId if it is provided.
    const selectedRobot = selectRobotById(rcDataState, props.homeId, props.robotId);

    // Find the robot definition for the selectedRobot
    const selectedRobotDefinition = getRobotDefinitionByRobotClass(robotCatalog, selectedRobot?.robotClass);

    // A flag to indicate if the selectedRobot is a Gateway robot. We need to do this check in many places so it is better to do it in 1 place here
    const isGatewayRobot = selectedRobotDefinition?.modeCommunicationType === RobotCommunicationType.GATEWAY;

    // Find the targetGateway if props.targetGatewayId is provided or the selectedRobot is a GATEWAY robot.
    const targetGateway = props.targetGatewayId || isGatewayRobot
        ? selectProjectDeviceById(dataState, props.targetGatewayId || selectedRobot?.deviceId as number) : undefined;

    // Find the gateway definition for the targetGateway
    const targetGatewayDefinition = useMemo(() => {
        if (gatewayCatalog && targetGateway) {
            return gatewayCatalog?.find((gatewayDef: GatewayDefinition) => {
                return gatewayDef.deviceClass === targetGateway.deviceClass;
            });
        }
        return undefined;
    }, [targetGateway, gatewayCatalog]);


    /**
     * Build a list of Robot Class options to be used for the select Robot Class Select input field.
     * If a targetGateway is selected, we will only show robotClasses that require gateways AND can be linked to the targetGateway
     * If a targetGateway is NOT selected, we will only show robotClass that DO NOT require gateway
     */
    const robotClassOptions = useMemo((): readonly SelectInputOption<string>[]| undefined => {
        if (robotCatalog && project) {

            // Create a robot class Option item for For each robotDefinition
            const options = robotCatalog.filter((robotDef: RobotDefinition): boolean => {
                if (props.targetGatewayId) {
                    // If there is a targetGatewayId, that mean we will create a robot AND link the robot to the targetGatewayId.
                    // Therefore, we need to filter out robot classes that do not need device/gateway because the robot classes
                    // can communicate with MODE cloud on their own without a gateway. And we also need to filter out robotClass that are
                    // not supported by the selectedGatewayDefinition.drivers
                    if (robotDef.modeCommunicationType === RobotCommunicationType.GATEWAY && targetGatewayDefinition) {

                        // Also exclude robotClass which is not supported by any of the the gateway's drivers
                        return targetGatewayDefinition.drivers?.find((driver: GatewayDriver) => {
                            return driver.robotClasses.includes(robotDef.robotClass);
                        }) !== undefined;
                    }

                    // robot class does not require gateway so exclude it
                    return false;
                }

                // If there is no specified props.targetGatewayId then we will just be creating a Robot without linking it to any gateway
                // This mean we will only allow DIRECT robot. Therefore, we will filter out robot classes that does not have communication
                // type === DIRECT
                return robotDef.modeCommunicationType === RobotCommunicationType.DIRECT;

            }).map((robotDef: RobotDefinition): SelectInputOption<string> => {
                return {
                    label: robotDef.name,
                    value: robotDef.robotClass,
                };
            });

            // If there is NO RobotClass option to select, add an option with label 'NO OPTION' and make it disabled
            return options.length > 0 ? options : [{
                label   : capitalizeAll(trans('label.no_options')).toUpperCase(),
                value   : '',
                disabled: true,
            }];
        }
        return undefined;
    }, [robotCatalog, project, trans, props.targetGatewayId, targetGatewayDefinition]);



    /**
     * Build a list of Driver options to be used for the "Driver Name" input field. This will only create the list of options if a gateway
     * is selected because the list of drivers is defined in the Gateway Catalog so if no gateway is selected, we don't know what driver the user
     * can select from.
     */
    const driverOptions = useMemo((): readonly SelectInputOption<string>[]| undefined => {
        const options = targetGatewayDefinition?.drivers?.map((driver: GatewayDriver) => {
            return {
                label: driver.driverName,
                value: driver.driverName,
            };
        });

        // If there is NO Driver Name option to select, add an option with label 'NO OPTION' and make it disabled
        return options && options.length > 0 ? options : [{
            label   : capitalizeAll(trans('label.no_options')).toUpperCase(),
            value   : '',
            disabled: true,
        }];

    }, [targetGatewayDefinition, trans]);



    const isNewRobot = !selectedRobot;
    const [inputErrors, setInputErrors] = useState<InputErrors>({});

    // Cache the user input each time they submit so we can use them in error messages
    const [userInputs, setUserInputs] = useState<RobotInfoUpdatableInputs | undefined>();


    // This useEffect is used for loading Device/Gateway info if the robot is a gateway robot
    useEffect(() => {
        if ((props.targetGatewayId || isGatewayRobot) && !targetGateway) {
            const gatewayId = props.targetGatewayId || selectedRobot?.deviceId;
            if (gatewayId) {
                dataDispatch(fetchProjectDeviceById(props.projectId, gatewayId)).catch(() => {
                // ignore errors
                });
            }
        }
    }, [dataDispatch, dataState, props.targetGatewayId, props.projectId, targetGateway, isGatewayRobot, selectedRobot]);


    /**
     * This useEffect is used for loading Robot data if it is not loaded. Also, we will only load Robot data if we have robotId. If not, this
     * container is probably used for creating new Robot
     */
    useEffect(() => {
        if (props.robotId && !selectedRobot) {
            rcDataDispatch(fetchRobotById(props.projectId, props.homeId, props.robotId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [selectedRobot, props.projectId, props.homeId, props.robotId, rcDataDispatch]);



    /**
     * This useEffect is used for loading gateway definition on component init
     */
    useEffect(() => {
        if (!gatewayCatalog && project) {
            rcDataDispatch(fetchRCProjectGatewayCatalog(project.id)).catch(() => {
                /* ignore error */
            });
        }
    }, [props.projectId, gatewayCatalog, project, rcDataDispatch]);


    /**
     * This useEffect is used for loading robot definition if it is not loaded
     */
    useEffect(() => {
        if (!robotCatalog) {
            rcDataDispatch(fetchRobotCatalog(props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [robotCatalog, props.projectId, rcDataDispatch]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * Automatically set editing mode to true on component init if props.robotId is undefined
     */
    useEffect(() => {
        if (props.robotId === undefined) {
            onChangeEditingMode(true);
        }
    }, [props.robotId, onChangeEditingMode]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({});

        onChangeEditingMode(false);
    }, [onChangeEditingMode]);



    /**
     * On Robot form input change, we will clear the error message for that field.
     */
    const onRobotInfoFormInputChange = useCallback((fieldName: string) => {
        setInputErrors(produce(inputErrors, (draft: any) => {
            draft[fieldName] = null;
        }));
    }, [inputErrors]);


    /**
     * On create Robot. Make an API call to create a new Robot with the provided info.
     * */
    const onCreateRobot = useCallback(async (
        id: string | undefined,
        name: string | undefined,
        robotClass: string | undefined,
        driverName: string | undefined,
        overrideRobotDefinition: string | undefined,
    ) => {
        try {
            // reset the errors
            const newInputErrors: InputErrors = ({});

            // Validate inputs
            if (!id) {
                newInputErrors.id = 'error.input.robot_id.empty';
            } else if (!ROBOT_ID_REGEX.test(id)) {
                newInputErrors.id = 'error.input.robot_id.invalid';
            }

            // find the robot definition for the new robot's robotClass
            const robotDefinition = getRobotDefinitionByRobotClass(robotCatalog, robotClass);

            if (!robotClass) {
                newInputErrors.robotClass = 'error.input.robot_class.empty';
            } else if (!robotDefinition) {
                // The selected robotClass is not defined in Robot Catalog
                newInputErrors.robotClass = 'error.input.robot_class.missing_robot_definition';
            } else if (robotDefinition.modeCommunicationType === RobotCommunicationType.DIRECT) {
                if (!deviceClasses?.find((deviceClass: DeviceClass) => {
                    return deviceClass.id === robotDefinition.robotClass;
                })) {
                    // The project does not have deviceClass associated with this robotClass. For DIRECT robot, there has to be a deviceClass
                    // that have the same ID as the robotClass
                    newInputErrors.robotClass = 'error.input.robot_class.missing_device_class';
                } else if (!project || !project.onDemandProvisioning) {
                    // For DIRECT robots, we need to be able to create new Devices on demand to link the new robot to the device
                    // therefore the project's provision type must be ONDEMAND_PROVISION
                    newInputErrors.robotClass = 'error.input.robot_class.invalid_device_provision';
                }
            }

            // driverName is only required for robots that require Gateway so if we know we are going to link this new robot to a
            // props.targetGatewayId then we need to validate driverName
            if (props.targetGatewayId) {
                if (!driverName) {
                    newInputErrors.driverName = 'error.input.gateway_driver_name.empty';
                } else if (robotClass) {
                    // If driverName and robotClass are provided, make sure they work together. The selected driver might not
                    // work for the selected robotClass.

                    const selectedDriver = targetGatewayDefinition?.drivers?.find((driver: GatewayDriver) => {
                        return driver.driverName === driverName;
                    });

                    // if the selected driver does not support for robotClass, show error
                    if (!selectedDriver || !selectedDriver.robotClasses.includes(robotClass)) {
                        newInputErrors.driverName = 'error.input.gateway_driver_name.invalid';
                    }
                }
            }

            // overrideRobotDefinition is OPTIONAL but if it is provided, it MUST be a valid JSON
            if (overrideRobotDefinition) {
                try {
                    JSON.parse(overrideRobotDefinition);
                } catch (error) {
                    newInputErrors.overrideRobotDefinition = 'error.input.robot_definition.invalid';
                }
            }

            // Update input errors if there are any
            setInputErrors(newInputErrors);

            if (robotCatalog && !newInputErrors.id && !newInputErrors.robotClass
                && !newInputErrors.overrideRobotDefinition
                && !newInputErrors.driverName && id && robotClass && robotDefinition && project) {

                // On device created and we have a deviceId, create a robot and link it to that device.
                // NOTE: if the robot require gateway then deviceId is not required
                const onDeviceCreated = async (deviceId?: number | undefined) => {
                    try {
                        await rcDataDispatch(createRobot(props.projectId, props.homeId, {
                            id,
                            deviceId               : deviceId || undefined,
                            name,
                            robotClass,
                            driverName             : driverName || undefined,
                            overrideRobotDefinition: overrideRobotDefinition ? JSON.parse(overrideRobotDefinition) : undefined,
                        }));

                        uiDispatch(showSuccessNotification('notif.generic_item_created_successfully', { term: 'label.robot' }));

                        closeEditing();
                        if (props.newRobotSettings) {
                            props.newRobotSettings.onNewRobotCreated();
                        }
                    } catch (error) {
                        // ignore error because the dataDispatch already handled it
                    }
                };


                if (robotDefinition.modeCommunicationType === RobotCommunicationType.DIRECT) {
                    // If robot communication type is DIRECT, we will need to create a device on demand and link the robot to that device.
                    // NOTE: For this flow, we will make an API call directly instead dispatching a Thunk action because it doesn't make
                    // sense for these calls to be Thunk actions. The result for these actions are only used right here and don't need to
                    // be cached in global state so we don't need to use Thunk
                    const provisionData = await AppAPI.getInstance().startDeviceProvisioning(props.homeId, robotClass);
                    const newDevice = await AppAPI.getInstance().addOndemandProvisionedHomeDevice(props.homeId, robotClass, provisionData.token);

                    // now call create robot and use the new device's id with the new robot
                    onDeviceCreated(newDevice.id);
                } else if (robotDefinition.modeCommunicationType === RobotCommunicationType.GATEWAY && props.targetGatewayId) {
                    // If robot communication type is GATEWAY AND if there is a selected gateway, we will automatically link the robot to the
                    // selected gateway.
                    onDeviceCreated(props.targetGatewayId);
                } else if (robotDefinition.modeCommunicationType === RobotCommunicationType.GATEWAY) {
                    // If there is no device selected, this mean we will be creating a new robot and will not link it to any gateway yet.
                    onDeviceCreated();
                }
            }
        } catch (error) {
            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
        }
    }, [robotCatalog, props.targetGatewayId, props.projectId, props.homeId, props.newRobotSettings, project, deviceClasses, targetGatewayDefinition,
        rcDataDispatch, uiDispatch, closeEditing]);



    /**
     * Update existing Robot
     */
    const onUpdateRobot = useCallback(async (
        robot: RobotInfo,
        name: string | undefined,
        driverName: string | undefined,
        robotDefinition: string | undefined,
    ) => {

        try {
            // Agent is updating existing Robot
            const newInputErrors: InputErrors = ({
                id: null, robotClass: null, overrideRobotDefinition: null,
            });


            // Validate inputs

            // driverName is only required for robots that require Gateway
            if (isGatewayRobot) {
                if (!driverName) {
                    newInputErrors.driverName = 'error.input.gateway_driver_name.empty';
                } else {
                    // If driverName and robotClass are provided, make sure they go together. The selected driver might not
                    // work for the selected robotClass.

                    const selectedDriver = targetGatewayDefinition?.drivers?.find((driver: GatewayDriver) => {
                        return driver.driverName === driverName;
                    });

                    // if the selected driver does not support for robotClass, show error
                    if (!selectedDriver || !selectedDriver.robotClasses.includes(robot.robotClass)) {
                        newInputErrors.driverName = 'error.input.gateway_driver_name.invalid';
                    }
                }
            }

            // RobotDefinition is OPTIONAL but if it is provided, it MUST be a valid JSON
            if (robotDefinition) {
                try {
                    JSON.parse(robotDefinition);
                } catch (error) {
                    newInputErrors.overrideRobotDefinition = 'error.input.robot_definition.invalid';
                }
            }

            // Update input errors if there are any
            setInputErrors(newInputErrors);

            if (!newInputErrors.driverName && !newInputErrors.overrideRobotDefinition) {
                // make sure the name or robotDefinition is different from the current value so that we don't waste an API call
                const nameChanged = name !== robot.name;
                const robotDefChanged = (robotDefinition && !robot.overrideRobotDefinition) || (!robotDefinition && robot.overrideRobotDefinition)
                                        || (robotDefinition && robot.overrideRobotDefinition
                                            && JSON.stringify(JSON.parse(robotDefinition)) !== JSON.stringify(robot.overrideRobotDefinition)
                                        );
                const driverNameChanged = driverName !== robot.driverName;

                if (nameChanged || driverNameChanged || robotDefChanged) {
                    await rcDataDispatch(updateRobot(props.projectId, robot.homeId, robot.id, {
                        driverName             : driverNameChanged ? driverName : undefined,
                        name                   : nameChanged ? name : undefined,
                        overrideRobotDefinition: robotDefChanged && robotDefinition ? JSON.parse(robotDefinition) : undefined,
                    }));
                    uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.robot' }));
                    closeEditing();
                } else {
                    // values are unchanged, no need to make API call
                    closeEditing();
                }
            }
        } catch (error) {
            /* ignore error */
        }
    }, [isGatewayRobot, targetGatewayDefinition, rcDataDispatch, props.projectId, uiDispatch, closeEditing]);



    /**
     * Handler for when the user click Save input from the Robot form
     */
    const onRobotInfoFormSubmit = useCallback(async (inputs: RobotInfoUpdatableInputs) => {
        if (!project) {
            return;
        }

        const { id, name, robotClass, driverName, robotDefinition } = inputs;
        setUserInputs(inputs);


        // First, check to see if we are using this container to create new Robot or updating an existing Robot. If creating new Robot, call
        // the appropriate function to handle the new Robot type.
        if (isNewRobot && props.newRobotSettings) {
            onCreateRobot(id, name, robotClass, driverName, robotDefinition);
        } else if (!isNewRobot && selectedRobot) {
            onUpdateRobot(selectedRobot, name, driverName, robotDefinition);
        }
    }, [props.newRobotSettings, selectedRobot, project, onCreateRobot, onUpdateRobot, isNewRobot]);


    /**
     * Create Input settings for the RobotInfoComp and cache it so that it doesn't get created on every re-render
     */
    const inputsSettings = useMemo((): RCRobotInfoProps['fields'] => {

        const defaultSettings: RCRobotInfoProps['fields'] = {
            id: {
                label       : capitalizeAll(trans('label.id')),
                displayValue: selectedRobot ? selectedRobot.id.toString() : '',
                value       : selectedRobot?.id || '',
                editable    : isNewRobot,
                required    : true,
                error       : inputErrors.id ? trans(inputErrors.id) : '',
                hideOnEdit  : props.minimalUI && !isNewRobot,
            },
            name: {
                label       : capitalizeAll(trans('label.name')),
                displayValue: selectedRobot?.name || '',
                value       : selectedRobot?.name,
                editable    : true,
            },
            robotClass: {
                label       : capitalizeAll(trans('label.robot_class')),
                displayValue: selectedRobot?.robotClass || '',
                value       : selectedRobot?.robotClass,
                options     : robotClassOptions,
                editable    : isNewRobot,
                required    : true,
                error       : inputErrors.robotClass ? trans(inputErrors.robotClass, { robot_class: userInputs?.robotClass }) : '',
                hideOnEdit  : props.minimalUI && !isNewRobot,
            },
            driverName: {
                label       : capitalizeAll(trans('label.gateway_driver_name')),
                displayValue: selectedRobot?.driverName || '',
                value       : selectedRobot?.driverName,
                options     : driverOptions,
                editable    : isNewRobot,
                required    : true,
                error       : inputErrors.driverName ? trans(inputErrors.driverName) : '',
                hidden      : (isNewRobot && !props.targetGatewayId) || (!isNewRobot && !isGatewayRobot),
            },
            robotDefinition: {
                label       : capitalizeAll(trans('label.robot_definition')),
                displayValue: selectedRobot ? JSON.stringify(selectedRobot.overrideRobotDefinition) : '',
                value       : selectedRobot ? JSON.stringify(selectedRobot.overrideRobotDefinition, undefined, 4) : '',
                editable    : true,
                error       : inputErrors.overrideRobotDefinition ? trans(inputErrors.overrideRobotDefinition) : '',
            },
        };

        if (selectedRobot && robotCatalog) {
            // find the selectedRobot's RobotDefinition from the list of robot definitions
            const robotDef = getRobotDefinitionByRobotClass(robotCatalog, selectedRobot.robotClass);


            // If the selectedRobot does not require gateway then we will assume the robot IS the device so we will show N/A instead of deviceId.
            const deviceIdDisplay = robotDef && robotDef.modeCommunicationType !== RobotCommunicationType.GATEWAY
                ? capitalizeAll(trans('label.not_applicable'))
                : selectedRobot.deviceId?.toString() || '';

            return {
                ...defaultSettings,
                homeId: {
                    label       : capitalizeAll(trans('label.home_id')),
                    displayValue: selectedRobot ? selectedRobot.homeId.toString() : '',
                    value       : selectedRobot.homeId,
                    hideOnEdit  : props.minimalUI,
                },
                deviceId: {
                    label       : capitalizeAll(trans('label.gateway_id')),   // we will only show "Gateway Id"
                    displayValue: deviceIdDisplay,
                    value       : selectedRobot.deviceId || undefined,
                    hideOnEdit  : props.minimalUI,
                },
                creationTime: {
                    label       : capitalizeAll(trans('label.date_created')),
                    displayValue: selectedRobot ? getDateTimeAndElapsedTime(selectedRobot.createdAt, trans) : '',
                    value       : selectedRobot.createdAt,
                    hideOnEdit  : props.minimalUI,
                },
                updatedTime: {
                    label       : capitalizeAll(trans('label.date_updated')),
                    displayValue: selectedRobot && selectedRobot.updatedAt ? getDateTimeAndElapsedTime(selectedRobot.updatedAt, trans) : '',
                    value       : selectedRobot.updatedAt,
                    hideOnEdit  : props.minimalUI,
                },
            };
        }


        return defaultSettings;
    }, [trans, selectedRobot, isNewRobot, inputErrors.id, inputErrors.robotClass, inputErrors.driverName, inputErrors.overrideRobotDefinition,
        props.minimalUI, props.targetGatewayId, robotClassOptions, userInputs, driverOptions, isGatewayRobot, robotCatalog]);


    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (props.newRobotSettings !== undefined) {
            props.newRobotSettings.onNewRobotCanceled();
        }
    }, [props.newRobotSettings, closeEditing]);



    // Create panel title
    const panelTitle = useMemo(() => {
        if (props.robotId) {
            return capitalizeAll(trans('label.robot_info'));
        }
        if (props.targetGatewayId) {
            return capitalizeAll(trans('label.create_robot_and_link_to_device', { deviceId: props.targetGatewayId }));
        }
        return capitalizeAll(trans('label.create_robot'));
    }, [props.robotId, props.targetGatewayId, trans]);


    return (
        <>
            {project && (selectedRobot || !props.robotId) && (
                <RCRobotInfo
                    className={clsx(props.className, props.infoCompProps?.className, isEditing && 'editing')}
                    formProps={props.infoCompProps?.formProps}
                    isEditing={isEditing}
                    title={panelTitle}
                    icon="mode-icon robot-solid"
                    fields={inputsSettings}
                    actions={{
                        startEdit: {
                            onClick: () => {
                                onChangeEditingMode(true);
                            },
                        },
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: onCancelEdit,
                        },
                        saveUpdate: {
                            label  : !selectedRobot ? capitalizeAll(trans('label.create')) : capitalizeAll(trans('label.save')),
                            onClick: onRobotInfoFormSubmit,
                        },
                    }}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={onRobotInfoFormInputChange}
                />
            )}

            {(!project || (props.robotId && !selectedRobot)) && (
                <CircularProgress />
            )}
        </>
    );
};
